import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { MagnifyingGlass, Stethoscope, CaretRight, ArrowRight } from "@phosphor-icons/react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { api } from "../lib/api";
import { SPECIALTY_CONTENT, ALL_SPECIALTIES, getSpecialtyContent } from "../lib/seoContent";

const POPULAR_TAXONOMIES = [
  "Internal Medicine", "Family Medicine", "Nurse Practitioner", "Dentist",
  "Optometry", "Chiropractic", "Physical Therapy", "Physician Assistant",
  "Clinical Psychology", "Social Worker", "Occupational Therapy", "Pharmacy",
  "Podiatry", "Speech-Language Pathology", "Acupuncture", "Registered Nurse",
  "Anesthesiology", "Ophthalmology", "Urology", "Gastroenterology",
  "Pulmonary Disease", "Endocrinology", "Rheumatology", "Nephrology",
  "Allergy & Immunology", "Infectious Disease", "Hematology", "Pain Medicine",
  "Sports Medicine", "Geriatric Medicine", "Hospice and Palliative Medicine",
  "Obstetrics & Gynecology", "Plastic Surgery", "Vascular Surgery",
  "Thoracic Surgery", "Oral & Maxillofacial Surgery",
];

const getSlug = (name) => name.toLowerCase().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-');

const SpecialtiesLanding = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [stateFilter, setStateFilter] = useState("");
  const [cityFilter, setCityFilter] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const handleSearch = useCallback(async () => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      setHasSearched(false);
      return;
    }
    setSearching(true);
    setHasSearched(true);
    try {
      const params = {
        taxonomy_description: searchQuery.trim(),
        limit: 10,
      };
      if (stateFilter) params.state = stateFilter;
      if (cityFilter.trim()) params.city = cityFilter.trim();

      const data = await api.searchProviders(params);
      // Extract unique specialties from results
      const seen = new Set();
      const specialties = [];
      (data.results || []).forEach(p => {
        (p.taxonomies || []).forEach(t => {
          if (t.desc && !seen.has(t.desc)) {
            seen.add(t.desc);
            specialties.push({ desc: t.desc, code: t.code });
          }
        });
      });
      setSearchResults(specialties);
    } catch {
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  }, [searchQuery, stateFilter, cityFilter]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery.trim().length >= 2) handleSearch();
      else { setSearchResults([]); setHasSearched(false); }
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery, handleSearch]);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "Healthcare Specialties Directory",
    "description": "Browse healthcare provider specialties. Find doctors, nurses, therapists and specialists across all medical disciplines in the United States.",
    "url": window.location.href,
  };

  return (
    <>
      <Helmet>
        <title>NPI Lookup by Specialty — Find Physician NPI Numbers</title>
        <meta name="description" content="NPI lookup by medical specialty. Find cardiologists, dermatologists, physicians, and specialist doctors with verified NPI numbers from the NPPES national registry." />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org", "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://npilookup.com/" },
            { "@type": "ListItem", "position": 2, "name": "Specialties" }
          ]
        })}</script>
      </Helmet>

      <div className="bg-slate-50 min-h-screen">
        {/* Hero Section */}
        <div className="bg-navy">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
            <nav className="mb-6 text-sm">
              <ol className="flex items-center gap-2 text-slate-400">
                <li><Link to="/" className="hover:text-white">Home</Link></li>
                <li>/</li>
                <li className="text-white font-medium">Specialties</li>
              </ol>
            </nav>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-teal rounded-xl flex items-center justify-center">
                <Stethoscope className="w-6 h-6 text-white" weight="bold" />
              </div>
              <h1 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl text-white" data-testid="specialties-landing-title">
                Healthcare Specialties
              </h1>
            </div>
            <p className="text-slate-300 text-lg max-w-2xl mb-8">
              Browse providers by medical specialty. Search any specialty, from primary care to highly specialized fields.
            </p>

            {/* Specialty Search */}
            <div className="max-w-2xl" data-testid="specialty-search">
              <div className="relative">
                <MagnifyingGlass className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  type="text"
                  placeholder="Search specialties (e.g., Gastroenterology, Nurse Practitioner...)"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-12 h-12 bg-white border-0 text-base rounded-xl shadow-lg"
                  data-testid="specialty-search-input"
                />
              </div>
              <div className="flex gap-3 mt-3">
                <Input
                  type="text"
                  placeholder="State (e.g., FL, NY)"
                  value={stateFilter}
                  onChange={(e) => setStateFilter(e.target.value.toUpperCase().slice(0, 2))}
                  className="h-10 bg-white/90 border-0 rounded-lg shadow text-sm w-32"
                  data-testid="specialty-state-input"
                />
                <Input
                  type="text"
                  placeholder="City (e.g., Miami)"
                  value={cityFilter}
                  onChange={(e) => setCityFilter(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  className="h-10 bg-white/90 border-0 rounded-lg shadow text-sm flex-1"
                  data-testid="specialty-city-input"
                />
              </div>

              {/* Live Search Results */}
              {hasSearched && (
                <div className="mt-3 bg-white rounded-xl shadow-lg overflow-hidden" data-testid="specialty-search-results">
                  {searching ? (
                    <div className="p-4 text-sm text-slate-500">Searching specialties...</div>
                  ) : searchResults.length > 0 ? (
                    <div className="divide-y divide-slate-100">
                      {searchResults.map((s, i) => {
                        const slug = getSlug(s.desc);
                        const params = new URLSearchParams();
                        if (stateFilter) params.set("state", stateFilter);
                        if (cityFilter.trim()) params.set("city", cityFilter.trim());
                        const qs = params.toString();
                        return (
                          <Link
                            key={i}
                            to={`/specialty/${slug}${qs ? `?${qs}` : ''}`}
                            className="flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors"
                            data-testid={`search-result-${i}`}
                          >
                            <div>
                              <p className="font-medium text-slate-900 text-sm">{s.desc}</p>
                              <p className="text-xs text-slate-400 mt-0.5">
                                {s.code && `Code: ${s.code}`}
                                {stateFilter && ` · ${stateFilter}`}
                                {cityFilter.trim() && ` · ${cityFilter.trim()}`}
                              </p>
                            </div>
                            <ArrowRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
                          </Link>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="p-4 text-sm text-slate-500">
                      No specialties found for "{searchQuery}". Try a different term.
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          {/* Featured Specialties with Descriptions */}
          <section data-testid="featured-specialties">
            <h2 className="font-display font-bold text-2xl text-slate-900 mb-6">
              Featured Specialties
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {ALL_SPECIALTIES.map(s => {
                const content = getSpecialtyContent(s.slug);
                return (
                  <Link
                    key={s.slug}
                    to={`/specialty/${s.slug}`}
                    className="group bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md hover:border-teal/30 transition-all"
                    data-testid={`featured-specialty-${s.slug}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-display font-semibold text-slate-900 group-hover:text-teal transition-colors">
                        {s.name}
                      </h3>
                      <CaretRight className="w-4 h-4 text-slate-400 group-hover:text-teal transition-colors" />
                    </div>
                    {content && (
                      <p className="text-sm text-slate-500 line-clamp-2 leading-relaxed">
                        {content.description}
                      </p>
                    )}
                  </Link>
                );
              })}
            </div>
          </section>

          {/* All Specialties / Taxonomy Browsing */}
          <section className="mt-12" data-testid="all-taxonomies">
            <h2 className="font-display font-bold text-2xl text-slate-900 mb-2">
              Browse All Specialties
            </h2>
            <p className="text-slate-500 mb-6">
              The NPI Registry includes hundreds of healthcare taxonomy codes. Here are common specialties you can browse.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
              {POPULAR_TAXONOMIES.map(tax => (
                <Link
                  key={tax}
                  to={`/specialty/${getSlug(tax)}`}
                  className="flex items-center justify-between px-4 py-3 bg-white rounded-lg border border-slate-200 hover:border-teal/30 hover:bg-teal/5 text-sm text-slate-700 hover:text-teal transition-all"
                  data-testid={`taxonomy-link-${getSlug(tax)}`}
                >
                  <span>{tax}</span>
                  <CaretRight className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                </Link>
              ))}
            </div>
          </section>

          {/* SEO Content */}
          <section className="mt-12 mb-8" data-testid="specialties-seo-content">
            <div className="bg-white rounded-xl border border-slate-200 p-6 sm:p-8">
              <h2 className="font-display font-bold text-xl text-slate-900 mb-4">
                Understanding Healthcare Specialties
              </h2>
              <div className="space-y-4 text-slate-600 leading-relaxed">
                <p>
                  Healthcare providers in the United States are classified by their medical specialty using
                  taxonomy codes maintained by the National Uniform Claim Committee (NUCC). Each provider
                  registered with the Centers for Medicare & Medicaid Services (CMS) has at least one taxonomy
                  code that describes their area of practice.
                </p>
                <p>
                  The NPI Registry contains over 6.2 million registered providers spanning hundreds of
                  specialties — from primary care physicians and nurse practitioners to highly specialized
                  fields like interventional cardiology and pediatric neurosurgery. Use our specialty directory
                  to browse providers by their field of expertise and find the right healthcare professional
                  for your needs.
                </p>
                <p>
                  Each specialty page shows registered providers with their verified NPI numbers, practice
                  locations, credentials, and contact information. You can further narrow your search by
                  state to find specialists near you.
                </p>
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
};

export default SpecialtiesLanding;
